import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import { ArrowRight, CheckCircle2, Camera, Users } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input, Label } from '@/components/ui/input';
import { apiClient, ApiError, type AffiliateApplicationCreate } from '@/lib/api';
import { PublicLayout } from '@/routes/_layout';

export function meta() {
  return [
    { title: 'Postularse como creador de contenido · Red de creadores de contenido Educrea' },
  ];
}

const COUNTRY_OPTIONS = [
  { value: '', label: 'Selecciona tu país' },
  { value: 'CL', label: 'Chile' },
  { value: 'AR', label: 'Argentina' },
  { value: 'MX', label: 'México' },
  { value: 'CO', label: 'Colombia' },
  { value: 'PE', label: 'Perú' },
  { value: 'UY', label: 'Uruguay' },
  { value: 'EC', label: 'Ecuador' },
  { value: 'VE', label: 'Venezuela' },
  { value: 'BO', label: 'Bolivia' },
  { value: 'PY', label: 'Paraguay' },
  { value: 'ES', label: 'España' },
  { value: 'US', label: 'Estados Unidos' },
];

const STATUS_OPTIONS = [
  { value: '', label: 'Selecciona tu audiencia' },
  { value: '<1k', label: 'Menos de 1.000 seguidores' },
  { value: '1k-5k', label: '1.000 - 5.000 seguidores' },
  { value: '5k-25k', label: '5.000 - 25.000 seguidores' },
  { value: '25k-100k', label: '25.000 - 100.000 seguidores' },
  { value: '100k+', label: 'Más de 100.000 seguidores' },
];

/**
 * Quita `@` inicial y espacios a los handles de redes sociales. Defensivo:
 * el backend ya normaliza en services/applications.ts, pero hacerlo acá
 * evita un round-trip cuando el usuario escribe algo que claramente es
 * "@miusuario" en lugar de "miusuario".
 */
function stripAtPrefix(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return trimmed;
  return trimmed.startsWith('@') ? trimmed.slice(1).trimStart() : trimmed;
}

/**
 * Mapea `details.fieldErrors` del backend (Zod flattenError) a un dict
 * { fieldName: firstMessage }. Si no hay field errors, devuelve `{}`.
 *
 * El backend ya viene shape:
 *   { fieldErrors: { instagram: ['Formato de Instagram inválido'], ... },
 *     formErrors: [...] }
 */
function extractFieldErrors(
  details: Record<string, unknown> | undefined,
): Record<string, string> {
  if (!details) return {};
  const fe = details.fieldErrors;
  if (!fe || typeof fe !== 'object') return {};
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(fe as Record<string, unknown>)) {
    if (Array.isArray(v) && typeof v[0] === 'string') {
      out[k] = v[0];
    } else if (typeof v === 'string') {
      out[k] = v;
    }
  }
  return out;
}

export default function ApplyPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState<1 | 2>(1);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [form, setForm] = useState<AffiliateApplicationCreate>({
    email: '',
    name: '',
    country: '',
    suggestedCode: '',
    rut: '',
    instagram: '',
    youtube: '',
    tiktok: '',
    facebook: '',
    linkedin: '',
    audienceSize: '',
    motivation: '',
  });

  /**
   * Update con dos responsabilidades:
   *  1. Strip `@` inicial en handles sociales (Instagram, TikTok,
   *     YouTube, Facebook, LinkedIn) — más amigable que el round-trip
   *     al backend.
   *  2. Limpiar el fieldError del campo en cuanto el usuario lo edita.
   *     Así el mensaje rojo desaparece apenas escribe.
   */
  const update = <K extends keyof AffiliateApplicationCreate>(
    key: K,
    value: AffiliateApplicationCreate[K],
  ) => {
    const SOCIAL_FIELDS = new Set([
      'instagram',
      'tiktok',
      'youtube',
      'facebook',
      'linkedin',
    ]);
    const normalized =
      SOCIAL_FIELDS.has(key as string) && typeof value === 'string'
        ? (stripAtPrefix(value) as AffiliateApplicationCreate[K])
        : value;
    setForm((f) => ({ ...f, [key]: normalized }));
    setFieldErrors((prev) => {
      if (!prev[key as string]) return prev;
      const { [key as string]: _drop, ...rest } = prev;
      return rest;
    });
  };

  const FieldError = ({ name }: { name: string }) => {
    const msg = fieldErrors[name];
    if (!msg) return null;
    return (
      <p
        role="alert"
        className="text-error text-xs"
        id={`${name}-error`}
      >
        {msg}
      </p>
    );
  };

  const onStep1Submit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!form.email || !form.name || !form.country) {
      setError('Email, nombre y país son obligatorios');
      return;
    }
    setStep(2);
  };

  const onStep2Submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setFieldErrors({});

    // Validación client-side: al menos una red social tiene que estar
    // completa. La regla la aplica también el backend (refine en
    // createApplicationSchema), pero avisamos antes para no hacer un
    // round-trip cuando el usuario se olvidó.
    const filledSocial = [
      form.instagram,
      form.youtube,
      form.tiktok,
      form.facebook,
      form.linkedin,
    ].some((v) => v && v.trim().length > 0);
    if (!filledSocial) {
      setError('Indicá al menos una red social donde te encuentran tus seguidores.');
      setFieldErrors({
        // Marcamos el primer campo social para que aparezca en rojo
        // y el usuario sepa dónde mirar. El mensaje general arriba
        // explica la regla.
        instagram: 'Indicá al menos una red social',
      });
      return;
    }

    setSubmitting(true);
    try {
      const result = await apiClient.submitApplication(form);
      navigate(`/apply/thanks?id=${result.id}`);
    } catch (err) {
      if (err instanceof ApiError) {
        // Mostrar el mensaje genérico arriba + los errores por campo
        // abajo del input correspondiente. Zod's flattenError agrupa
        // por field name; si hay algo raro, caemos al mensaje genérico.
        const fe = extractFieldErrors(err.details);
        if (Object.keys(fe).length > 0) {
          setFieldErrors(fe);
          setError(
            'Hay datos inválidos. Revisá los campos marcados en rojo.',
          );
        } else {
          setError(err.message);
        }
      } else {
        setError('Hubo un error al enviar tu postulación. Intentá de nuevo.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass = (field: string) =>
    cn(
      'flex h-10 w-full rounded-md border px-3 py-2 text-sm',
      fieldErrors[field]
        ? 'border-error bg-error/5 focus-visible:border-error focus-visible:ring-error/20'
        : 'border-border bg-surface focus-visible:border-primary focus-visible:ring-primary/20',
    );

  return (
    <PublicLayout>
      <section className="bg-primary-light">
        <div className="mx-auto max-w-3xl px-6 py-12 sm:py-16">
          <div className="bg-primary/10 text-primary mb-3 inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wider">
            Programa de creadores
          </div>
          <h1 className="text-foreground text-4xl font-extrabold tracking-tight sm:text-5xl">
            Postúlate como creador/a de contenido
          </h1>
          <p className="text-muted-foreground mt-3 text-lg">
            Promové los cursos de Educrea y ganá comisiones por cada venta. Completá el formulario
            en 2 pasos y te avisaremos por email cuando revisemos tu postulación.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-3xl px-6 py-12">
        {error && (
          <div className="border-error/20 bg-error/5 text-error mb-4 rounded-md border p-3 text-sm">
            {error}
          </div>
        )}

        <Card>
          <CardHeader>
            <CardTitle>
              {step === 1 ? 'Paso 1 de 2: Datos básicos' : 'Paso 2 de 2: Tu presencia online'}
            </CardTitle>
            <CardDescription>
              {step === 1
                ? 'Empezamos con lo esencial. Lo demás es opcional pero nos ayuda a conocerte mejor.'
                : 'Indicá al menos una red social donde te encuentran tus seguidores. Lo demás es opcional.'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {step === 1 ? (
              <form onSubmit={onStep1Submit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="country">País *</Label>
                  <select
                    id="country"
                    required
                    value={form.country}
                    onChange={(e) => update('country', e.target.value)}
                    className={inputClass('country')}
                  >
                    {COUNTRY_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value} disabled={o.value === ''}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                  <FieldError name="country" />
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      required
                      value={form.email}
                      onChange={(e) => update('email', e.target.value)}
                      aria-invalid={Boolean(fieldErrors.email)}
                      aria-describedby={fieldErrors.email ? 'email-error' : undefined}
                    />
                    <FieldError name="email" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="name">Nombre completo *</Label>
                    <Input
                      id="name"
                      required
                      minLength={2}
                      value={form.name}
                      onChange={(e) => update('name', e.target.value)}
                      aria-invalid={Boolean(fieldErrors.name)}
                      aria-describedby={fieldErrors.name ? 'name-error' : undefined}
                    />
                    <FieldError name="name" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="rut">RUT/ID (opcional)</Label>
                  <Input
                    id="rut"
                    placeholder="12.345.678-9"
                    value={form.rut ?? ''}
                    onChange={(e) => update('rut', e.target.value)}
                    aria-invalid={Boolean(fieldErrors.rut)}
                    aria-describedby={fieldErrors.rut ? 'rut-error' : undefined}
                  />
                  <FieldError name="rut" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="code">Código sugerido (opcional)</Label>
                  <Input
                    id="code"
                    placeholder="ej. MI-CODIGO"
                    minLength={6}
                    maxLength={20}
                    value={form.suggestedCode ?? ''}
                    onChange={(e) => update('suggestedCode', e.target.value)}
                    aria-invalid={Boolean(fieldErrors.suggestedCode)}
                    aria-describedby={fieldErrors.suggestedCode ? 'code-error' : undefined}
                  />
                  <p className="text-muted-foreground text-xs">
                    Si tenés un alias, lo registramos. Si no, te asignamos uno.
                  </p>
                  <FieldError name="suggestedCode" />
                </div>
                <div className="flex justify-end pt-2">
                  <Button type="submit">
                    Continuar <ArrowRight className="h-4 w-4" />
                  </Button>
                </div>
              </form>
            ) : (
              <form onSubmit={onStep2Submit} className="space-y-4">
                <div className="grid gap-4 sm:grid-cols-3">
                  <div className="space-y-2">
                    <Label htmlFor="instagram">Instagram</Label>
                    <Input
                      id="instagram"
                      placeholder="ej: educrea.cl (sin @)"
                      value={form.instagram ?? ''}
                      onChange={(e) => update('instagram', e.target.value)}
                      aria-invalid={Boolean(fieldErrors.instagram)}
                      aria-describedby={fieldErrors.instagram ? 'instagram-error' : undefined}
                    />
                    <FieldError name="instagram" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="youtube">YouTube / canal</Label>
                    <Input
                      id="youtube"
                      placeholder="ej: canal o URL completa"
                      value={form.youtube ?? ''}
                      onChange={(e) => update('youtube', e.target.value)}
                      aria-invalid={Boolean(fieldErrors.youtube)}
                      aria-describedby={fieldErrors.youtube ? 'youtube-error' : undefined}
                    />
                    <FieldError name="youtube" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="tiktok">TikTok</Label>
                    <Input
                      id="tiktok"
                      placeholder="ej: educrea.cl (sin @)"
                      value={form.tiktok ?? ''}
                      onChange={(e) => update('tiktok', e.target.value)}
                      aria-invalid={Boolean(fieldErrors.tiktok)}
                      aria-describedby={fieldErrors.tiktok ? 'tiktok-error' : undefined}
                    />
                    <FieldError name="tiktok" />
                  </div>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="facebook">Facebook</Label>
                    <Input
                      id="facebook"
                      placeholder="ej: usuario o URL completa"
                      value={form.facebook ?? ''}
                      onChange={(e) => update('facebook', e.target.value)}
                      aria-invalid={Boolean(fieldErrors.facebook)}
                      aria-describedby={fieldErrors.facebook ? 'facebook-error' : undefined}
                    />
                    <FieldError name="facebook" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="linkedin">LinkedIn</Label>
                    <Input
                      id="linkedin"
                      placeholder="ej: tu-perfil o URL completa"
                      value={form.linkedin ?? ''}
                      onChange={(e) => update('linkedin', e.target.value)}
                      aria-invalid={Boolean(fieldErrors.linkedin)}
                      aria-describedby={fieldErrors.linkedin ? 'linkedin-error' : undefined}
                    />
                    <FieldError name="linkedin" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="audience">Tamaño de audiencia</Label>
                  <select
                    id="audience"
                    value={form.audienceSize ?? ''}
                    onChange={(e) => update('audienceSize', e.target.value)}
                    className={inputClass('audienceSize')}
                    aria-invalid={Boolean(fieldErrors.audienceSize)}
                    aria-describedby={fieldErrors.audienceSize ? 'audience-error' : undefined}
                  >
                    {STATUS_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                  <FieldError name="audienceSize" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="motivation">¿Por qué querés ser creador/a de contenido?</Label>
                  <textarea
                    id="motivation"
                    rows={4}
                    className={`border-border bg-surface flex w-full rounded-md border px-3 py-2 text-sm ${
                      fieldErrors.motivation
                        ? 'border-error bg-error/5 focus-visible:border-error focus-visible:ring-error/20'
                        : ''
                    }`}
                    placeholder="Contanos un poco sobre tu audiencia y por qué te interesa el programa..."
                    value={form.motivation ?? ''}
                    onChange={(e) => update('motivation', e.target.value)}
                    aria-invalid={Boolean(fieldErrors.motivation)}
                    aria-describedby={fieldErrors.motivation ? 'motivation-error' : undefined}
                  />
                  <FieldError name="motivation" />
                </div>
                <div className="flex justify-between pt-2">
                  <Button type="button" variant="outline" onClick={() => setStep(1)}>
                    Atrás
                  </Button>
                  <Button type="submit" disabled={submitting}>
                    {submitting ? 'Enviando...' : 'Enviar postulación'}
                  </Button>
                </div>
              </form>
            )}
          </CardContent>
        </Card>

        <div className="mt-8 grid gap-4 sm:grid-cols-3">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="text-success h-5 w-5 shrink-0" />
            <div>
              <div className="text-foreground font-medium">Revisión manual</div>
              <p className="text-muted-foreground text-sm">
                Nuestro equipo revisa cada postulación
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Camera className="text-success h-5 w-5 shrink-0" />
            <div>
              <div className="text-foreground font-medium">Comisión</div>
              <p className="text-muted-foreground text-sm">
                Por venta atribuida, con detalle para tu validación
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Users className="text-success h-5 w-5 shrink-0" />
            <div>
              <div className="text-foreground font-medium">Acompañamiento</div>
              <p className="text-muted-foreground text-sm">Materiales y soporte dedicado</p>
            </div>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}

// Local cn (avitamos agregar import circular si no está ya)
function cn(...inputs: Array<string | false | null | undefined>) {
  return inputs.filter(Boolean).join(' ');
}
